import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  IConfigPerfil,
  IDimmerCalendarioConfig,
  IDispositivoLuminariaACTIS,
  IModoLuminaria,
  NivelObjetivo,
  PayloadSetModeActis,
} from 'modelos/src';
import { HelperService } from './helper.service';
import { DialogService } from '../dialog/dialog.service';
import {
  ConfirmarEnvioManualComponent,
  DataConfirmarEnvioManual,
} from '../dialog/confirmar-envio-manual/confirmar-envio-manual.component';
import { LuminariasHttpService } from './http/luminarias.service';
import { LuminariasService } from '../../modulos/entidades/luminarias/service';
import { PotenciaModalComponent } from '../../modulos/entidades/luminarias/luminarias/potencia-modal/potencia-modal.component';
import { DimmerCalendarNewComponent } from '../../modulos/entidades/luminarias/luminarias/dimmer-calendario/dimmer-nuevo.component';
import { EditarConfigLuminariaGPEComponent } from '../../modulos/entidades/luminarias/luminarias/editar-config-luminaria-gpe/editar-config-luminaria-gpe.component';
import { CambioModoComponent } from '../../modulos/entidades/luminarias/luminarias/cambio-modo/cambio-modo.component';
import { CambioModoActisComponent } from '../../modulos/entidades/luminarias/luminarias/cambio-modo-actis/cambio-modo-actis.component';
import { ConfigActisComponent } from '../../modulos/entidades/luminarias/luminarias/configuraciones-actis/configuraciones-actis.component';
import {
  TipoConfigActis,
  TipoConsultaActis,
} from '../../modulos/entidades/luminarias/luminarias.types';
import { FormularioDialogService } from './formulario-dialog.service';

// Objetivo de un comando: cualquier "contenedor" que se expande a luminarias.
export interface Objetivo {
  id: string;
  nivel: NivelObjetivo; // 'luminaria' | 'grupo' | 'puesta' | 'grupoPuesta'
  nombre?: string;
}

// Contexto del objetivo necesario para los flujos de perfil.
// - perfilesConfig: perfiles asignados directamente al objetivo (grupo.perfilConfigs / puesta.perfilConfigs)
// - tiposCount: tipoDispositivo → cantidad de luminarias del objetivo
// - lumConPerfilPorTipo: tipoDispositivo → ids de luminarias del objetivo con perfil PROPIO
export interface ObjetivoComandoCtx {
  perfilesConfig?: IConfigPerfil[];
  tiposCount?: Map<string, number>;
  lumConPerfilPorTipo?: Map<string, string[]>;
}

// Servicio compartido con TODOS los comandos por objetivo. Lo usan los listados
// de grupos (grupo de luminarias / grupo de puestas), y el listado de puestas.
// Una sola implementación → sin duplicación ni drift.
@Injectable({ providedIn: 'root' })
export class ComandosObjetivoService {
  private formularios = inject(FormularioDialogService);
  private dialogService = inject(DialogService);
  private helper = inject(HelperService);
  private luminariasService = inject(LuminariasHttpService);
  private luminariasFlowService = inject(LuminariasService);

  private readonly TIPO_GPE = 'Luminaria GPE';
  private readonly TIPO_ACTIS = 'Luminaria ACTIS FING';
  private readonly TIPO_WELLNESS = 'Luminaria Wellness';

  private ok() {
    this.helper.notifSuccess('Comando enviado');
  }
  private err(error: unknown) {
    console.error(error);
    this.helper.notifError(error);
  }

  // Antes de un comando MANUAL de configuración sobre un objetivo grupal
  // (grupo/puesta/grupo de puestas SIN perfil aplicable), informa con datos
  // reales del backend (jerarquía) a cuántas luminarias del tipo alcanzará el
  // comando: las que tienen perfil (propio o de un nivel superior) se ignoran
  // por jerarquía, y los valores enviados pasan a ser la configuración deseada
  // de las alcanzadas. Devuelve false si el usuario cancela.
  private async confirmarEnvioManual(
    o: Objetivo,
    tipo: string,
    comandoNombre: string,
  ): Promise<boolean> {
    // A nivel luminaria individual el flujo propio ya avisa (desvincular perfil).
    if (o.nivel === 'luminaria') return true;
    // El diálogo se abre de inmediato y consulta la jerarquía con un spinner.
    const data: DataConfirmarEnvioManual = {
      comandoNombre,
      tipo,
      nivel: o.nivel,
      id: o.id,
    };
    const ref = this.formularios.abrir(ConfirmarEnvioManualComponent, {
      tamano: 'chico',
      data,
    });
    const res = await firstValueFrom(ref.afterClosed());
    return res === true;
  }

  // ── WELLNESS ────────────────────────────────────────────────────────────

  public async encenderWellness(o: Objetivo): Promise<void> {
    const ref = this.formularios.abrir(PotenciaModalComponent, {
      tamano: 'chico',
      data: {
        actual: 50,
        modo: 'encender',
        luminaria: o.nombre,
        tipo: this.TIPO_WELLNESS,
      },
    });
    const result = await firstValueFrom(ref.afterClosed());
    if (result === undefined || result === null) return;
    const dimmer = typeof result === 'object' ? result.dimmer : result;
    try {
      await this.luminariasService.encenderWellness(o, dimmer);
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async apagarWellness(o: Objetivo): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Confirmá la acción',
      `¿Apagar las luminarias Wellness de ${o.nombre}?`,
    );
    if (!ok) return;
    try {
      await this.luminariasService.apagarWellness(o);
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  // ── GPE ─────────────────────────────────────────────────────────────────

  public async encenderGPE(o: Objetivo): Promise<void> {
    const ref = this.formularios.abrir(PotenciaModalComponent, {
      tamano: 'chico',
      data: { actual: 50, modo: 'encender', luminaria: o.nombre },
    });
    const result = await firstValueFrom(ref.afterClosed());
    if (result === undefined || result === null) return;
    const dimmer = typeof result === 'object' ? result.dimmer : result;
    try {
      await this.luminariasService.encenderGPE(o, dimmer);
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async apagarGPE(o: Objetivo): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Confirmá la acción',
      `¿Apagar las luminarias GPE de ${o.nombre}?`,
    );
    if (!ok) return;
    try {
      await this.luminariasService.apagarGPE(o);
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async cambiarModoGPE(o: Objetivo): Promise<void> {
    const seguir = await this.confirmarEnvioManual(
      o,
      this.TIPO_GPE,
      'Cambio de modo GPE',
    );
    if (!seguir) return;
    const ref = this.formularios.abrir(CambioModoComponent, {
      tamano: 'chico',
      data: { modoActual: 'Manual' },
    });
    const response = await firstValueFrom(ref.afterClosed());
    if (!response) return;
    try {
      await this.luminariasService.cambiarModoGPE(
        o,
        response as IModoLuminaria,
      );
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async consultarConfigGPE(o: Objetivo): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Confirmá la acción',
      `¿Consultar la configuración de las luminarias GPE de ${o.nombre}?`,
    );
    if (!ok) return;
    try {
      await this.luminariasService.consultarConfigGPE(o);
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async createConfigGPE(o: Objetivo): Promise<void> {
    const seguir = await this.confirmarEnvioManual(
      o,
      this.TIPO_GPE,
      'Configuración general GPE',
    );
    if (!seguir) return;
    this.formularios.abrir(EditarConfigLuminariaGPEComponent, {
      tamano: 'mediano',
      data: {
        edit: { _id: o.id },
        soloCambioModo: false,
        esGrupo: true,
        nivelObjetivo: o.nivel,
      },
    });
  }

  public async configCalendarioGPE(o: Objetivo): Promise<void> {
    const initialConfig: IDimmerCalendarioConfig = {
      calendarioHabilitado: true,
      puntos: [{ hora: '00:00', porcentaje: 50, activo: true }],
      porcentajeDefecto: 50,
    };
    const seguirManual = await this.confirmarEnvioManual(
      o,
      this.TIPO_GPE,
      'Configuración calendario GPE',
    );
    if (!seguirManual) return;
    const ref = this.formularios.abrir(DimmerCalendarNewComponent, {
      tamano: 'grande',
      data: { initialConfig },
    });
    const result = await firstValueFrom(ref.afterClosed());
    if (result === undefined) return;
    try {
      await this.luminariasService.createConfigCalendarioGPE(
        o,
        result as IDimmerCalendarioConfig,
      );
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async consultarConfigCalendarioGPE(o: Objetivo): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Confirmá la acción',
      `¿Consultar el calendario de las luminarias GPE de ${o.nombre}?`,
    );
    if (!ok) return;
    try {
      await this.luminariasService.consultarConfigCalendarioGPE(o);
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async pedirGPSGPE(o: Objetivo): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Confirmá la acción',
      `¿Pedir GPS de las luminarias GPE de ${o.nombre}?`,
    );
    if (!ok) return;
    try {
      await this.luminariasService.pedirGPSGPE(o);
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async resetGPE(o: Objetivo): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Confirmá la acción',
      `¿Resetear las luminarias GPE de ${o.nombre}?`,
    );
    if (!ok) return;
    try {
      await this.luminariasService.resetGPE(o);
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async resetEnergiaGPE(o: Objetivo): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Confirmá la acción',
      `¿Resetear el contador de energía de las luminarias GPE de ${o.nombre}?`,
    );
    if (!ok) return;
    try {
      await this.luminariasService.resetEnergiaGPE(o);
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  // ── ACTIS ─────────────────────────────────────────────────────────────────

  public async encenderActis(o: Objetivo): Promise<void> {
    const ref = this.formularios.abrir(PotenciaModalComponent, {
      tamano: 'chico',
      data: {
        actual: 50,
        modo: 'encender',
        luminaria: o.nombre,
        tipo: this.TIPO_ACTIS,
      },
    });
    const result = await firstValueFrom(ref.afterClosed());
    if (result === undefined || result === null) return;
    const dimmer = typeof result === 'object' ? result.dimmer : result;
    const sf =
      typeof result === 'object' ? result.salirPorFotocelula : undefined;
    const sr =
      typeof result === 'object' ? result.salirPorRelojAstronomico : undefined;
    try {
      await this.luminariasService.encenderActis(o, dimmer, sf, sr);
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async apagarActis(o: Objetivo): Promise<void> {
    const ref = this.formularios.abrir(PotenciaModalComponent, {
      tamano: 'chico',
      data: {
        actual: 0,
        modo: 'apagar',
        luminaria: o.nombre,
        tipo: this.TIPO_ACTIS,
      },
    });
    const result = await firstValueFrom(ref.afterClosed());
    if (!result) return;
    try {
      await this.luminariasService.apagarActis(
        o,
        result.salirPorFotocelula,
        result.salirPorRelojAstronomico,
      );
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async cambiarModoActis(o: Objetivo): Promise<void> {
    const seguirManual = await this.confirmarEnvioManual(
      o,
      this.TIPO_ACTIS,
      'Cambio de modo ACTIS',
    );
    if (!seguirManual) return;
    const ref = this.formularios.abrir(CambioModoActisComponent, {
      tamano: 'mediano',
      data: {},
    });
    const response = await firstValueFrom(ref.afterClosed());
    if (!response) return;
    try {
      await this.luminariasService.setModeActis(
        o,
        response as PayloadSetModeActis,
      );
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  public async configActis(
    o: Objetivo,
    config: TipoConfigActis,
  ): Promise<void> {
    const meta: Record<
      TipoConfigActis,
      {
        comandoNombre: string;
        send: (payload: IDispositivoLuminariaACTIS) => Promise<unknown>;
      }
    > = {
      Offset: {
        comandoNombre: 'Configuración astronómica',
        send: (payload) => this.luminariasService.setOffsetActis(o, payload),
      },
      Treshold: {
        comandoNombre: 'Configuración fotocélula',
        send: (payload) => this.luminariasService.setTresholdActis(o, payload),
      },
      Coordinates: {
        comandoNombre: 'Configuración coordenadas',
        send: (payload) =>
          this.luminariasService.setCoordinatesActis(o, payload),
      },
      Reporte: {
        comandoNombre: 'Configuración de reportes',
        send: (payload) =>
          this.luminariasService.setReportConfigActis(o, payload),
      },
      ACK: {
        comandoNombre: 'Configuración de ACKs',
        send: (payload) => this.luminariasService.setACKConfigActis(o, payload),
      },
    };

    const m = meta[config];
    const seguirManual = await this.confirmarEnvioManual(
      o,
      this.TIPO_ACTIS,
      m.comandoNombre,
    );
    if (!seguirManual) return;
    const ref = this.formularios.abrir(ConfigActisComponent, {
      tamano: 'mediano',
      data: { config },
    });
    const response = await firstValueFrom(ref.afterClosed());
    if (response) {
      try {
        await m.send(response as IDispositivoLuminariaACTIS);
        this.ok();
      } catch (e) {
        this.err(e);
      }
    }
  }

  public async consultaActis(
    o: Objetivo,
    config: TipoConsultaActis,
  ): Promise<void> {
    const seleccion =
      await this.luminariasFlowService.pedirSeleccionConsultaActisPublic(
        config,
      );
    if (!seleccion) return;
    try {
      if (config === 'configGeneral') {
        await this.luminariasService.consultarConfigGeneralActis(o, seleccion);
      } else if (config === 'estado') {
        await this.luminariasService.consultarEstadoActis(o, seleccion);
      } else {
        await this.luminariasService.consultarConfigActis(o, seleccion);
      }
      this.ok();
    } catch (e) {
      this.err(e);
    }
  }

  // ── PERFIL (GPE + ACTIS) ────────────────────────────────────────────────

  // Reaplicar el perfil efectivo a TODAS las luminarias del objetivo. No manda
  // downlinks al instante: invalida en el backend la config real conocida de
  // cada dispositivo para que el reconciliador detecte la divergencia y reenvíe
  // los SET. Útil tras una actualización de firmware masiva, donde el nodo
  // pierde su configuración pero el sistema sigue creyéndola aplicada.
  public async reaplicarPerfil(o: Objetivo): Promise<void> {
    const ok = await this.dialogService.confirm(
      `¿Reaplicar el perfil a las luminarias de ${o.nombre}?`,
      `Se reenviará a cada dispositivo con perfil efectivo toda la configuración definida en su perfil.<br>
       Útil cuando los dispositivos perdieron su configuración (por ejemplo, tras un cambio de firmware).<br><br>
       El cambio no es inmediato: se aplicará cuando se complete la próxima reconciliación de configuración de cada dispositivo.`,
    );
    if (!ok) return;
    try {
      const r = await this.luminariasService.reaplicarPerfil(o);
      this.helper.notifSuccess(
        `Reaplicación iniciada en ${r?.reaplicadas ?? 0} luminaria(s). La configuración se restablecerá en la próxima reconciliación.`,
      );
    } catch (e) {
      this.err(e);
    }
  }
}
